import React, { useMemo, useState } from "react";
import { useVariableValue } from "@devcycle/react-client-sdk";
import { isEqual, startCase } from "lodash";
import { Flex } from "styles/utility-components";
import useDeepCompareEffect from "use-deep-compare-effect";

import { InfoButton } from "components/actions/InfoButton";
import { Currency } from "components/app_layout/Typography";
import { FrameworkCell } from "components/frameworks/FrameworkCell";
import BuyerCategoriesWithPopover from "components/table_components/BuyerCategoriesWithPopover";
import MultipleEntityLinks from "components/table_components/MultipleEntityLinks";
import { NewTitleCell } from "components/table_components/NewTitleCell";
import SuppliersContainer from "components/table_components/SuppliersContainer";
import SignalsContainer from "components/tags/SignalsContainer";
import RedactData from "components/ui/redact_data/RedactData";
import { dateSort, numberSort, stringSort } from "lib/columnSort";
import NoticeStage from "lib/core_components/NoticeStage";
import { ColumnType, commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { Table } from "lib/core_components/Table/Table";
import { RELEVANCY_SEARCH_NEW_USERS } from "lib/featureFlags";
import { useRestricedViewNotice } from "lib/hooks/useRestrictedRowClick";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { ProHelperDataTypes } from "lib/providers/ProHelper";
import { SortState } from "lib/search/types";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { NoticesStages } from "lib/types/graphQLEnums";
import { formatDate, formatDateOrOpenEnded } from "lib/utils";
import { getSignalScoreTooltipText, SignalEntityType } from "lib/utils/signalUtils";
import { NewRelevanceScore } from "../../lib/core_components/NewRelevanceScore";
import { NoticesDto, useNotices } from "../../lib/hooks/api/notices/useNotices";
import { SaveLeadDrawerProvider } from "../leads/saving/SaveLeadDrawerProvider";
import { ViewLeadsDrawerProvider } from "../leads/viewing/ViewLeadsDrawerProvider";
import { NoticeActions } from "./NoticeActions";
import {
  convertNoticeFiltersToSearchRequest,
  DEFAULT_NOTICE_SORT_ORDER,
  getNoticeColumnCopy,
  mapNoticeForActions,
  NoticeColumns,
  NoticeFilters,
} from "./utils";

type NoticeColumnsMap = {
  [key in NoticeColumns]: ColumnType<NoticesDto>;
};

function getSortOrder(sort: SortState, key: string): "ascend" | "descend" | null {
  return sort.field !== key ? null : sort.order === "ASC" ? "ascend" : "descend";
}

function getNoticeColumns(
  filters: NoticeFilters,
  columnSettings: NoticeColumns[],
  requiredDataType: ProHelperDataTypes | undefined,
  isRelevancySearchEnabled: boolean,
): ColumnType<NoticesDto>[] {
  const { sort } = filters;
  const title: ColumnType<NoticesDto> = {
    ...commonTableColumns.titleColumn,
    title: getNoticeColumnCopy("title"),
    key: "title",
    dataIndex: "name",
    render: (_, record) => (
      <NewTitleCell notice={record} requiredDataType={requiredDataType} filters={filters} />
    ),
    sorter: stringSort((r: NoticesDto) => r.name),
    sortOrder: getSortOrder(sort, "title"),
    showSorterTooltip: false,
  };

  const stage: ColumnType<NoticesDto> = {
    ...commonTableColumns.stageColumn,
    title: getNoticeColumnCopy("stage"),
    dataIndex: "stage",
    key: "stage",
    render: (_, record) => {
      return <NoticeStage stage={record.stagev2 ?? undefined} />;
    },
    showSorterTooltip: false,
  };

  const value: ColumnType<NoticesDto> = {
    ...commonTableColumns.valueColumn,
    title: getNoticeColumnCopy("value"),
    dataIndex: "value",
    key: "value",
    render: (_, r) => (
      <Flex justifyContent="end">
        <RedactData isRedacted={r.redacted}>
          <Currency longFormat value={r.value} currency={r.currency || undefined} />
        </RedactData>
      </Flex>
    ),
    sorter: numberSort((r: NoticesDto) => r.value, getSortOrder(sort, "value")),
    sortOrder: getSortOrder(sort, "value"),
    sortDirections: ["descend", "ascend", "descend"],
    showSorterTooltip: false,
  };

  const relevance_score: ColumnType<NoticesDto> = {
    ...commonTableColumns.relevanceColumn,
    title: getNoticeColumnCopy("relevance_score"),
    key: "relevance_score",
    dataIndex: "score",
    render: (_, r) => (
      <NewRelevanceScore
        relevanceScore={r.score}
        id={r.guid}
        entityType={SignalEntityType.ProcurementNotice}
        eventAttributes={{
          "Notice guid": r.guid,
          Stage: r.procurementStage?.stage,
          "Signal score": r.score ?? 0,
          "Data type": EventDataTypes.record,
        }}
      />
    ),
    sorter: numberSort((r: NoticesDto) => r.score),
    sortOrder: getSortOrder(sort, "relevance_score"),
    sortDirections: ["descend", "ascend", "descend"],
    showSorterTooltip: {
      title: getSignalScoreTooltipText("notice"),
    },
  };

  const close_date: ColumnType<NoticesDto> = {
    ...commonTableColumns.dateColumn,
    key: "close_date",
    dataIndex: "closeDate",
    title: getNoticeColumnCopy("close_date"),
    render: (_, r) => (
      <RedactData isRedacted={r.redacted}>{formatDateOrOpenEnded(r.closeDate)}</RedactData>
    ),
    sorter: stringSort((r) => r.closeDate),
    sortOrder: getSortOrder(sort, "close_date"),
  };

  const published_date: ColumnType<NoticesDto> = {
    ...commonTableColumns.dateColumn,
    key: "publish_date",
    title: getNoticeColumnCopy("publish_date"),
    render: (_, r) => (
      <RedactData isRedacted={r.redacted}>
        {r.publishDate ? formatDate(new Date(r.publishDate)) : r.publishDate}
      </RedactData>
    ),
    sorter: dateSort((r) => r.publishDate),
    sortOrder: getSortOrder(sort, "publish_date"),
  };

  const award_date: ColumnType<NoticesDto> = {
    ...commonTableColumns.dateColumn,
    key: "award_date",
    title: getNoticeColumnCopy("award_date"),
    render: (_, r) => (
      <RedactData isRedacted={r.redacted}>
        {r.awardDate ? formatDate(new Date(r.awardDate)) : r.awardDate}
      </RedactData>
    ),
    sorter: stringSort((r) => r.awardDate),
    sortOrder: getSortOrder(sort, "award_date"),
  };

  const expiry_date: ColumnType<NoticesDto> = {
    ...commonTableColumns.dateColumn,
    key: "expiry_date",
    title: getNoticeColumnCopy("expiry_date"),
    render: (_, r) => (
      <RedactData isRedacted={r.redacted}>{formatDateOrOpenEnded(r.expiryDate)}</RedactData>
    ),
    sorter: stringSort((r) => r.expiryDate),
    sortOrder: getSortOrder(sort, "expiry_date"),
  };

  const qualification: ColumnType<NoticesDto> = {
    ...commonTableColumns.qualification,
    title: getNoticeColumnCopy("qualification"),
    key: "qualification",
    showSorterTooltip: false,
    render: (_, r) => {
      return (
        <RedactData isRedacted={r.redacted} bullets={12}>
          <NoticeActions notice={mapNoticeForActions(r)} contextSource="In-row" size="small" />
        </RedactData>
      );
    },
  };

  const country: ColumnType<NoticesDto> = {
    ...commonTableColumns.countryColumn,
    title: getNoticeColumnCopy("country"),
    key: "country",
    render: (_, r) => <RedactData isRedacted={r.redacted}>{r.country}</RedactData>,
    sortOrder: getSortOrder(sort, "country"),
    sorter: stringSort((r) => r.country),
  };

  const is_sme: ColumnType<NoticesDto> = {
    ...commonTableColumns.isSMEColumn,
    title: (
      <div>
        SME suppliers?{" "}
        <InfoButton
          note={
            <div>
              For contracts awarded to multiple suppliers, this will show "Yes" if any of the
              suppliers are SMEs
            </div>
          }
        />
      </div>
    ),
    key: "isSme",
    render: (_, r) => (
      <RedactData isRedacted={r.redacted}>
        {r.stage === NoticesStages.Awarded
          ? r.suppliers?.some((s) => s.isSme)
            ? "Yes"
            : "No"
          : "N/A"}
      </RedactData>
    ),
  };

  const suppliers: ColumnType<NoticesDto> = {
    ...commonTableColumns.supplierColumn,
    title: getNoticeColumnCopy("suppliers"),
    key: "suppliers",
    render: (_, r) => {
      return <SuppliersContainer recordName={r.name ?? "--"} suppliers={r.suppliers || []} />;
    },
  };

  const buyer_categories: ColumnType<NoticesDto> = {
    key: "buyer_type",
    title: getNoticeColumnCopy("buyer_categories"),
    width: "210px",
    render: (r: NoticesDto) => (
      <RedactData isRedacted={r.redacted}>
        <MultipleEntityLinks
          title={`Types for ${r.buyers.map((b) => b.name).join(", ")}`}
          links={r.buyers.map((b) => (
            <BuyerCategoriesWithPopover
              size="default"
              buyerCategories={b.categoryAssignments.map((assignment) => ({
                buyer_category_id: assignment.categoryId,
                source: assignment.source,
              }))}
              buyerName={b.name}
              key={b.guid}
            />
          ))}
        />
      </RedactData>
    ),
  };

  const framework: ColumnType<NoticesDto> = {
    title: getNoticeColumnCopy("framework"),
    key: "framework",
    sizeConfig: {
      xlarge: 300,
      large: 250,
      medium: 200,
      small: 170,
    },
    showSorterTooltip: false,
    render: (_, r) => {
      return r.relatedFrameworkProcess ? (
        <FrameworkCell
          frameworkId={r.relatedFrameworkProcess.frameworkId}
          frameworkName={r.relatedFrameworkProcess.frameworkName}
          processType={r.relatedFrameworkProcess.processType}
          filters={filters}
        />
      ) : null;
    },
  };

  // Has to be called indicator to match the column name in NoticeColumns
  const indicator: ColumnType<NoticesDto> = {
    title: getNoticeColumnCopy("indicator"),
    key: "signals",
    sizeConfig: {
      xlarge: 250,
      large: 150,
      medium: 150,
      small: 150,
    },
    showSorterTooltip: false,
    render: (_, r) => {
      return (
        <RedactData isRedacted={r.redacted} bullets={12}>
          <SignalsContainer signals={r.signals || []} contextSource="In-row" />
        </RedactData>
      );
    },
  };

  const columnTypeMap: Partial<NoticeColumnsMap> = {
    title,
    stage,
    close_date,
    published_date,
    expiry_date,
    award_date,
    value,
    country,
    suppliers,
    is_sme,
    qualification,
    buyer_categories,
    ...(isRelevancySearchEnabled ? {} : { relevance_score }),
    framework,
    indicator,
  };

  const columns: ColumnType<NoticesDto>[] = [];

  for (const column of columnSettings) {
    const columnType = columnTypeMap[column];
    if (columnType) {
      columns.push(columnType);
    }
  }

  return columns;
}

export const DEFAULT_PAGINATION = { current: 1, pageSize: 10 };

export function NoticeTable({
  filters,
  columnSettings,
  onSortChange,
  selectedRows,
  onSelectedRowsChange,
  isPreview,
  requiredDataType,
  emptyText,
}: {
  filters: NoticeFilters;
  columnSettings: NoticeColumns[];
  onSortChange?: (sort: SortState) => void;
  selectedRows?: string[];
  onSelectedRowsChange?: (selectedRowKeys: string[], selectedRows: NoticesDto[]) => void;
  isPreview?: boolean;
  requiredDataType?: ProHelperDataTypes;
  emptyText?: JSX.Element;
}) {
  const { logEvent } = useTracking();
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useTablePageSize("notice-table");

  const includeSignals = columnSettings.includes("indicator");

  const {
    data: noticeSearch,
    isLoading,
    isError,
  } = useNotices(
    convertNoticeFiltersToSearchRequest(filters, { current: currentPage, pageSize }),
    includeSignals,
  );

  const columns = useMemo(
    () =>
      getNoticeColumns(
        {
          ...filters,
          sort: {
            ...filters.sort,
            field: filters.sort.field || DEFAULT_NOTICE_SORT_ORDER.field,
            order: filters.sort.order || DEFAULT_NOTICE_SORT_ORDER.order,
          },
        },
        columnSettings,
        requiredDataType,
        isRelevancySearchEnabled,
      ),
    [filters, columnSettings, requiredDataType, isRelevancySearchEnabled],
  );

  useDeepCompareEffect(() => {
    onSelectedRowsChange?.([], []);
    setCurrentPage(1);
  }, [filters]);

  const { viewRecord } = useRestricedViewNotice(requiredDataType);
  const { viewRecord: showPaywallModal } = useRestricedViewNotice("Row-click", "AWARDS");

  return (
    <SaveLeadDrawerProvider trackingData={{ "Context source": "In-row" }}>
      <ViewLeadsDrawerProvider>
        <Table<NoticesDto>
          ariaLabel="Notice Table"
          dataSource={noticeSearch?.results}
          loading={isLoading}
          columns={columns}
          showSorterTooltip={false}
          rowKey={(record) => record.procurementStage.id}
          onRow={(record) => ({
            onClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
              if (record.redacted) {
                showPaywallModal(record.guid);
              } else {
                viewRecord(record.guid);
              }
            },
          })}
          pagination={{
            current: currentPage,
            pageSize,
            total: isPreview ? pageSize : noticeSearch?.pagingInfo.totalResults || 0,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
              logEvent(EventNames.paginationSelected, { Value: page });
            },
            showSizeChanger: true,
          }}
          onChange={(pag, __, sorter) => {
            if (currentPage !== pag.current || pageSize !== pag.pageSize) {
              // pagination has changed which is handled by the pagination onChange event
              return;
            }
            if (Array.isArray(sorter)) {
              sorter = sorter[0];
            }
            // If the sort order is not defined, we don't want to trigger a sort without an order
            if (!filters.sort && !sorter.order) {
              return;
            }
            const newSortState: SortState = {
              order: sorter.order === "descend" ? "DESC" : "ASC",
              field: sorter.columnKey as string,
            };
            if (!isEqual(filters.sort, newSortState)) {
              onSortChange?.(newSortState);
              logEvent(EventNames.tableSorted, {
                Context: "Column action",
                "Sorted by": startCase(newSortState.field),
                "Sort order": newSortState.order,
              });
            }
          }}
          rowSelection={
            onSelectedRowsChange &&
            selectedRows && {
              onChange: (selectedRowKeys, selectedRows) => {
                onSelectedRowsChange(selectedRowKeys as string[], selectedRows);
              },
              preserveSelectedRowKeys: true,
              selectedRowKeys: selectedRows,
            }
          }
          isError={isError}
          locale={{
            emptyText,
          }}
        />
      </ViewLeadsDrawerProvider>
    </SaveLeadDrawerProvider>
  );
}
